/*
  The lookbook's family, its orientation and the register's Active choice, each defined once and
  mirroring the API's string values (enums travel as strings). The web holds no category-to-family
  table of its own: the server works it out from the category's code (LookbookFamily.cs) and sends
  it as `kind` on every category, row and record.
*/

export const LOOKBOOK_FAMILY = {
    pages: 'Pages',
    photos: 'Photos',
    video: 'Video',
    products: 'Products',
} as const;

export type LookbookFamily = (typeof LOOKBOOK_FAMILY)[keyof typeof LOOKBOOK_FAMILY];

export const LOOKBOOK_ORIENTATION = {
    none: 'None',
    landscape: 'Landscape',
    portrait: 'Portrait',
} as const;

export type LookbookOrientation = (typeof LOOKBOOK_ORIENTATION)[keyof typeof LOOKBOOK_ORIENTATION];

/** What one file of a media book is (ALS-79), as the API sends `kind` on every file. */
export const LOOKBOOK_MEDIA_KIND = {
    photo: 'Photo',
    video: 'Video',
} as const;

export type LookbookMediaKind = (typeof LOOKBOOK_MEDIA_KIND)[keyof typeof LOOKBOOK_MEDIA_KIND];

export const LOOKBOOK_ACTIVE = {
    all: 'All',
    yes: 'Yes',
    no: 'No',
} as const;

export type LookbookActiveFilter = (typeof LOOKBOOK_ACTIVE)[keyof typeof LOOKBOOK_ACTIVE];

/** Only a page book carries a message the client reads with it. */
export function takesMessage(family: LookbookFamily | null): boolean {
    return family === LOOKBOOK_FAMILY.pages;
}

/**
 * How much a book holds, in its own unit: a product book's products, every other book's pages or
 * files. One rule for the register, the delete dialog and the export, which drifted apart when
 * each worked it out alone (the export counted a product book's pages, ALS-298).
 */
export function contentOf(book: {
    kind: LookbookFamily;
    contentCount: number;
    productCount: number;
}): number {
    return book.kind === LOOKBOOK_FAMILY.products ? book.productCount : book.contentCount;
}

/** Only the media books say how the app lays their files out. */
export function takesOrientation(family: LookbookFamily | null): boolean {
    return family === LOOKBOOK_FAMILY.photos || family === LOOKBOOK_FAMILY.video;
}
